import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreFormActions from "@store/Components/Store/StoreFormActions";
import StoreHistoryPageMobileButtons from "@store/Components/Store/StoreHistoryPageMobileButtons";
import { PaymentHistoryPageContext } from "@store/Pages/PaymentHistoryPage";
import { GetPaymentHistoryApiParameter } from "@store/Services/PaymentHistoryService";
import { getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import clearSearch from "@store/Store/Pages/PaymentHistory/Handlers/ClearSearch";
import toggleFiltersModalOpen from "@store/Store/Pages/PaymentHistory/Handlers/ToggleFiltersModalOpen";
import toggleFiltersOpen from "@store/Store/Pages/PaymentHistory/Handlers/ToggleFiltersOpen";
import updateDirtyFilters from "@store/Store/Pages/PaymentHistory/Handlers/UpdateDirtyFilters";
import updateSearchFields from "@store/Store/Pages/PaymentHistory/Handlers/UpdateSearchFields";
import { getPaymentHistoryParameters } from "@store/Store/Pages/PaymentHistory/PaymentHistorySelectors";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { contentSpacingStyles, tagWrapperStyles } from "@store/theme/common/mixins";
import { datepickerWrapperStyles } from "@store/theme/common/mixins/datepicker";
import { historyFilterProps } from "@store/theme/common/props/historyFilter";
import { mobileFiltersModalProps } from "@store/theme/common/props/mobileFiltersModal";
import { TYPOGRAPHY } from "@store/theme/design";
import getDateTimeFormat from "@store/utilities/getDateTimeFormat";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showCheckNumber = "showCheckNumber",
    showCheckAmount = "showCheckAmount",
    showDateRange = "showDateRange",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showCheckNumber]: boolean;
        [fields.showCheckAmount]: boolean;
        [fields.showDateRange]: boolean;
    };
}

interface State {
    checkNumber: string;
    checkAmount: string;
}

const mapStateToProps = (state: StoreApplicationState) => ({
    filtersOpen: state.store.pages.paymentHistory.filtersOpen,
    getPaymentHistoryParameter: getPaymentHistoryParameters(state),
    activePaymentHistoryParameter: state.store.pages.paymentHistory.getPaymentHistoryParameter,
    dirtyFilters: state.store.pages.paymentHistory.dirtyFilters,
    isFiltersModalOpen: state.store.pages.paymentHistory.isFiltersModalOpen,
    parameter: state.pages.orderApprovalList.getOrderApprovalsParameter,
    dateFormatDisplay: getStoreSettingsCollection(state).poolcorpSettings.dateFormatDisplay,
    datePickerFormat: getStoreSettingsCollection(state).poolcorpSettings.dateFormatDisplay,
});

const mapDispatchToProps = {
    updateSearchFields,
    clearSearch,
    toggleFiltersOpen,
    updateDirtyFilters,
    toggleFiltersModalOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext;

export interface PaymentHistoryFilterStyles {
    container?: GridContainerProps;
    heading?: TypographyProps;
    headingItem?: GridItemProps;
    desktopFilterInputFieldsGridItem?: GridItemProps;
    filterInputFieldsGridItem?: GridItemProps;
    paymentNumberText?: TextFieldProps;
    checkNumberText?: TextFieldProps;
    desktopDatePickerGridItem?: GridItemProps;
    datePickerGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    onlyOpenGridItem?: GridItemProps;
    onlyOpenCheckbox?: CheckboxProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonProps;
    applyButton?: ButtonProps;
    gridItem?: GridItemProps;
    filtersModal?: ModalPresentationProps;
    modalTitleWrapper?: InjectableCss;
    modalTitle?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
    modalClearFiltersButton?: ButtonPresentationProps;
    modalApplyButton?: ButtonPresentationProps;
    modalClearButton?: ButtonPresentationProps;
}

export const filterStyles: PaymentHistoryFilterStyles = {
    container: historyFilterProps.gridContainer,
    gridItem: historyFilterProps.gridItem,
    filtersModal: mobileFiltersModalProps.modal,
    modalTitleWrapper: mobileFiltersModalProps.modalTitleWrapper,
    modalTitle: mobileFiltersModalProps.modalTitle,
    buttonsWrapper: mobileFiltersModalProps.buttonsWrapper,
    modalApplyButton: mobileFiltersModalProps.primaryButton,
    modalClearButton: mobileFiltersModalProps.secondaryButton,
    modalClearFiltersButton: mobileFiltersModalProps.secondaryButton,
    headingItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    heading: {
        variant: "h5",
        as: "h2",
        css: css`
            margin: 0;
        `,
    },
    filterInputFieldsGridItem: historyFilterProps.subGridItem,
    datePickerGridItem: {
        ...historyFilterProps.subGridItem,
        css: css`
            ${historyFilterProps.subGridItem?.css}
            ${datepickerWrapperStyles()}
        `,
    },
    desktopFilterInputFieldsGridItem: {
        ...historyFilterProps.subGridItem,
        width: 3,
    },
    desktopDatePickerGridItem: {
        ...historyFilterProps.subGridItem,
        width: 3,
        css: css`
            ${historyFilterProps.subGridItem?.css}
            ${datepickerWrapperStyles()}
        `,
    },
    onlyOpenGridItem: { width: 12 },
    onlyOpenCheckbox: {
        typographyProps: {
            weight: TYPOGRAPHY.weight.bold,
        },
    },
    appliedFiltersContainer: {
        css: css`
            ${tagWrapperStyles()}
            ${contentSpacingStyles(TYPOGRAPHY.spacing.base, null)}
        `,
    },
    buttonsItem: {},
    applyButton: historyFilterProps.applyButton,
    clearFiltersButton: historyFilterProps.clearButton,
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const styles = filterStyles;
const tzOffset = new Date().getTimezoneOffset() * 60000;
class PaymentHistoryFilter extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            checkNumber: this.getValue(props, "checkNumber"),
            checkAmount: this.getValue(props, "checkAmount"),
        };
    }

    updateTimeoutId: number | undefined;

    getValue = (props: Props, key: "checkNumber" | "checkAmount"): string => {
        let value = props.getPaymentHistoryParameter[key] as string;
        if (!value) {
            value = "";
        }
        return value;
    };

    componentDidUpdate(prevProps: Props) {
        const previousCheckNumber = this.getValue(prevProps, "checkNumber");
        const currentCheckNumber = this.getValue(this.props, "checkNumber");
        const previousCheckAmount = this.getValue(prevProps, "checkAmount");
        const currentCheckAmount = this.getValue(this.props, "checkAmount");

        if (
            (previousCheckNumber !== currentCheckNumber && currentCheckNumber !== this.state.checkNumber) ||
            (previousCheckAmount !== currentCheckAmount && currentCheckAmount !== this.state.checkAmount)
        ) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                checkNumber: currentCheckNumber,
                checkAmount: currentCheckAmount,
            });
        }
    }

    updateParameterAfterTimeout = () => {
        if (typeof this.updateTimeoutId === "number") {
            clearTimeout(this.updateTimeoutId);
        }

        this.updateTimeoutId = setTimeout(() => {
            this.props.updateSearchFields({
                checkNumber: this.state.checkNumber,
                checkAmount: this.state.checkAmount,
            });
        }, 250);
    };

    updateFilters = (parameters: GetPaymentHistoryApiParameter, update?: boolean) => {
        if (update) {
            this.props.updateSearchFields(parameters);
            this.closeModal();
        }

        this.props.updateDirtyFilters(parameters);
    };
    clearFilters = () => {
        clearSearch();
        this.closeModal();
    };

    closeModal = () => {
        if (this.props.isFiltersModalOpen) {
            this.props.toggleFiltersModalOpen();
        }
    };

    checkNumberChangeHandler = (checkNumber: string, update?: boolean) => {
        this.setState({
            checkNumber,
        });

        this.updateFilters({ checkNumber }, update);
    };
    checkAmountChangeHandler = (checkAmount: string, update?: boolean) => {
        this.setState({
            checkAmount,
        });

        this.updateFilters({ checkAmount }, update);
    };

    fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">, update?: boolean) => {
        this.updateFilters(
            {
                fromDate: selectedDay ? new Date(selectedDay.getTime() - tzOffset).toISOString().split("T")[0] : "",
            },
            update,
        );
    };

    toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">, update?: boolean) => {
        this.updateFilters(
            {
                toDate: selectedDay ? new Date(selectedDay.getTime() - tzOffset).toISOString().split("T")[0] : "",
            },
            update,
        );
    };

    clearFiltersClickHandler = () => {
        this.checkNumberChangeHandler("", false);
        this.checkAmountChangeHandler("", false);
        this.fromDateChangeHandler({}, false);
        this.toDateChangeHandler({}, false);
        this.props.clearSearch();
        this.closeModal();
    };

    render() {
        const FilterTags = (
            <StyledWrapper {...styles.appliedFiltersContainer}>
                {this.props.activePaymentHistoryParameter.checkNumber && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            this.checkNumberChangeHandler("", true);
                        }}
                    >
                        {`${translate("Check Number")}: ${this.props.activePaymentHistoryParameter.checkNumber}`}
                    </Tag>
                )}
                {this.props.activePaymentHistoryParameter.checkAmount && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            this.checkAmountChangeHandler("", true);
                        }}
                    >
                        {`${translate("Check Amount")}: ${this.props.activePaymentHistoryParameter.checkAmount}`}
                    </Tag>
                )}
                {this.props.activePaymentHistoryParameter.fromDate && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            this.fromDateChangeHandler({}, true);
                        }}
                        data-test-selector="paymentHistory_fromDateButton"
                    >
                        {`${translate("From")}: ${getDateTimeFormat({
                                dateTime: this.props.activePaymentHistoryParameter.fromDate.replace(/-/g, "/"),
                                dateFormat: this.props.dateFormatDisplay,
                        })}`}
                    </Tag>
                )}
                {this.props.activePaymentHistoryParameter.toDate && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            this.toDateChangeHandler({}, true);
                        }}
                    >
                        {`${translate("To")}: ${getDateTimeFormat({
                                dateTime: this.props.activePaymentHistoryParameter.toDate.replace(/-/g, "/"),
                                dateFormat: this.props.dateFormatDisplay,
                        })}`}
                    </Tag>
                )}
            </StyledWrapper>
        );

        const fromDate = this.props.getPaymentHistoryParameter.fromDate
            ? new Date(this.props.getPaymentHistoryParameter.fromDate.replace(/-/g, "/"))
            : undefined;
        const toDate = this.props.getPaymentHistoryParameter.toDate
            ? new Date(this.props.getPaymentHistoryParameter.toDate.replace(/-/g, "/"))
            : undefined;

        let fromMinDate: Date;
        if (toDate) {
            fromMinDate = new Date(toDate);
            fromMinDate.setFullYear(toDate.getFullYear() - 1);
        }

        let toMaxDate: Date;
        if (fromDate) {
            toMaxDate = new Date(fromDate);
            toMaxDate.setFullYear(fromDate.getFullYear() + 1);
        }

        const applyFilters = () => {
            this.props.updateSearchFields(this.props.dirtyFilters);
            this.closeModal();
        };

        const formFieldsGrid = (isMobileForm: boolean) =>
        (
            <GridContainer {...styles.container}>
                {this.props.fields.showCheckNumber && (
                    <GridItem {...isMobileForm ? styles.filterInputFieldsGridItem : styles.desktopFilterInputFieldsGridItem}>
                        <TextField
                            {...styles.checkNumberText}
                            value={this.state.checkNumber}
                            label={!isMobileForm ? translate("Check Number") : ""}
                            placeholder={translate("Check Number")}
                            onChange={event => this.checkNumberChangeHandler(event.target.value)}
                        />
                    </GridItem>
                )}
                {this.props.fields.showCheckAmount && (
                    <GridItem {...isMobileForm ? styles.filterInputFieldsGridItem : styles.desktopFilterInputFieldsGridItem}>
                        <TextField
                            {...styles.checkNumberText}
                            value={this.state.checkAmount}
                            label={!isMobileForm ? translate("Check Amount") : ""}
                            placeholder={translate("Check Amount")}
                            onChange={event => this.checkAmountChangeHandler(event.target.value)}
                        />
                    </GridItem>
                )}
                {this.props.fields.showDateRange && (
                    <>
                        <GridItem
                            {...isMobileForm ? styles.datePickerGridItem : styles.desktopDatePickerGridItem}
                            data-test-selector="paymentHistory_fromDateFilter"
                        >
                            <DatePicker
                                {...styles.fromDate}
                                label={translate("Date From")}
                                format={this.props.datePickerFormat}
                                selectedDay={fromDate}
                                onDayChange={this.fromDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: null,
                                    maxDate: toDate,
                                    minDate: fromMinDate,
                                    ...styles.fromDate?.dateTimePickerProps,
                                }}
                            />
                        </GridItem>
                        <GridItem {...isMobileForm ? styles.datePickerGridItem : styles.desktopDatePickerGridItem} data-test-selector="paymentHistory_toDateFilter">
                            <DatePicker
                                {...styles.toDate}
                                label={translate("Date To")}
                                format={this.props.datePickerFormat}
                                selectedDay={toDate}
                                onDayChange={this.toDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: null,
                                    minDate: fromDate,
                                    maxDate: toMaxDate,
                                    ...styles.toDate?.dateTimePickerProps,
                                }}
                            />
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
        return (
            <>
                <Hidden above="md">
                    <StoreHistoryPageMobileButtons
                        primaryButtonLabel={translate("Filters")}
                        onPrimaryButtonClick={() => this.props.toggleFiltersModalOpen()}
                    />
                    <Modal
                        isOpen={this.props.isFiltersModalOpen}
                        handleClose={this.closeModal}
                        {...styles.filtersModal}
                    >
                        <StyledWrapper {...styles.modalTitleWrapper}>
                            <Typography {...styles.modalTitle} data-test-selector="productListFilterList">
                                {translate("Filters")}
                            </Typography>
                        </StyledWrapper>
                        {formFieldsGrid(true)}
                        <StyledWrapper {...styles.buttonsWrapper}>
                            <Button {...styles.modalApplyButton} onClick={applyFilters}>
                                {translate("Apply")}
                            </Button>
                            <Button
                                {...styles.modalClearFiltersButton}
                                onClick={this.clearFiltersClickHandler}
                                data-test-selector="paymentHistory_clearFiltersButton"
                            >
                                {translate("Clear Filters")}
                            </Button>
                        </StyledWrapper>
                    </Modal>
                </Hidden>
                <Hidden below="lg">
                    <Accordion headingLevel={2}>
                        <AccordionSection
                            title={translate("Filters")}
                            expanded={this.props.filtersOpen}
                            onTogglePanel={this.props.toggleFiltersOpen}
                        >
                            {formFieldsGrid(false)}
                            <StoreFormActions>
                                <Button {...styles.applyButton} onClick={applyFilters}>
                                    {translate("Apply")}
                                </Button>
                                <Button
                                    {...styles.clearFiltersButton}
                                    onClick={this.clearFiltersClickHandler}
                                    data-test-selector="paymentHistory_clearFiltersButton"
                                >
                                    {translate("Clear Filters")}
                                </Button>
                            </StoreFormActions>
                        </AccordionSection >
                    </Accordion >
                </Hidden>
                {FilterTags}
            </>
        )
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PaymentHistoryFilter),
    definition: {
        group: "Payment History" as any,
        displayName: "Search Results Filter",
        allowedContexts: [PaymentHistoryPageContext] as any[],
        fieldDefinitions: [
            {
                name: fields.showCheckNumber,
                displayName: "Check #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showCheckAmount,
                displayName: "Check Amount",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showDateRange,
                displayName: "Date Range",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
    },
};

export default widgetModule;
